import React from "react";
import { Link } from "react-router-dom";

/**
 * Iter XX.190 — Privacy Policy expansion for Google Play submission.
 *
 * Adds the disclosures the Play Console Data Safety review requires:
 *   · Meta Pixel (advertising analytics)
 *   · OpenAI / Emergent (AI response generation)
 *   · Stripe (payments processing)
 *   · Resend (transactional email)
 *   · In-app account deletion pointer to /account/delete
 *   · Fixed effective date
 *   · Contact email
 *   · Minimum age of use (13+)
 *
 * Only practices actually verified in the codebase are stated. Tone
 * preserved from the original page.
 */
const EFFECTIVE_DATE = "8 February 2026";
const CONTACT_EMAIL = "support@gullyapp.com";

export default function Privacy() {
  return (
    <div className="grain min-h-screen">
      <header className="border-b" style={{ borderColor: "var(--border-default)" }}>
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="font-display text-xl text-neutral-100" data-testid="legal-home-link">gully</Link>
          <Link to="/terms" className="text-sm text-neutral-400 hover:text-neutral-200">Terms</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16" data-testid="privacy-page">
        <p className="label-eyebrow mb-3">privacy</p>
        <h1 className="font-display font-light text-3xl sm:text-4xl tracking-tight mb-2">How we treat your conversations.</h1>
        <p className="text-neutral-500 mb-12 text-sm" data-testid="privacy-effective-date">
          Effective {EFFECTIVE_DATE}.
        </p>

        <div className="space-y-10 text-neutral-300 leading-relaxed">
          <section>
            <h2 className="font-display text-lg text-neutral-100 mb-3">Your chats are yours</h2>
            <p>What you tell Gully — your matches, your nets, your nerves — stays private. We don't sell it. We don't share it with advertisers. We don't show it to other users.</p>
          </section>

          <section>
            <h2 className="font-display text-lg text-neutral-100 mb-3">How we use the data</h2>
            <p>We use what you message Gully to do two things:</p>
            <ul className="mt-3 space-y-2 pl-1">
              <li>· Coach you better next time — your past conversations help Gully remember patterns in your game.</li>
              <li>· Improve the product — anonymous, aggregated insights help us make Gully a better cricket companion. Anything we publish or share publicly is fully de-identified.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-lg text-neutral-100 mb-3">Please don't share sensitive stuff</h2>
            <p>Gully is a cricket coach, not a doctor or therapist. Please don't share medical records, mental-health diagnoses, financial information, government IDs, or anything you'd be uncomfortable seeing in a coaching context. If something serious is going on, please talk to a qualified professional.</p>
          </section>

          <section>
            <h2 className="font-display text-lg text-neutral-100 mb-3">What we store</h2>
            <p>Your phone number (optional), the name you give us, your cricket profile (role, playing level), the messages you send Gully, the reports Gully writes for you, and — only if you upload one — the cricket videos you share with Gully. That's it.</p>
          </section>

          <section data-testid="privacy-ai-disclosure">
            <h2 className="font-display text-lg text-neutral-100 mb-3">How Gully generates responses</h2>
            <p>
              Gully's coaching replies are produced by large language
              models from <strong>OpenAI</strong>, routed to us through
              our AI infrastructure provider, <strong>Emergent
              Labs</strong>. When you send a message, that message and
              your recent conversation history are sent to those
              providers to generate a reply.
            </p>
            <p className="mt-3">
              When you upload a cricket video for review, the video
              may be processed by <strong>Google Gemini</strong>{" "}
              through our AI infrastructure for the purpose of
              generating cricket-specific analysis, and the audio
              track may be transcribed by <strong>OpenAI Whisper</strong>.
              Google is not affiliated with Gully and does not
              endorse, sponsor, or operate this app; Gemini is used
              solely as a processing service to help generate your
              analysis.
            </p>
            <p className="mt-3">
              These providers process your text, audio and video
              strictly to produce your reply or analysis. They do
              not use your conversations or videos to train their
              public models on the plans we use. Please still avoid
              sharing anything you would not want an AI provider to
              process.
            </p>
          </section>

          <section data-testid="privacy-payments-disclosure">
            <h2 className="font-display text-lg text-neutral-100 mb-3">Payments</h2>
            <p>
              If you choose to subscribe to Gully Plus, payments are
              handled by <strong>Stripe</strong>. Your card number,
              CVV and full billing details go directly to Stripe — we
              never see or store them. We only record whether your
              subscription is active and when it expires, so we know
              what to unlock for you.
            </p>
          </section>

          <section data-testid="privacy-email-disclosure">
            <h2 className="font-display text-lg text-neutral-100 mb-3">Emails we send you</h2>
            <p>
              Transactional emails (welcome message, receipts,
              occasional weekly reflection summaries) are delivered
              through <strong>Resend</strong>. Only your email
              address, name, and the email content itself are shared
              with Resend for delivery. You can unsubscribe from
              non-essential emails at any time from the footer of any
              email we send.
            </p>
          </section>

          <section data-testid="privacy-clarity-disclosure">
            <h2 className="font-display text-lg text-neutral-100 mb-3">Analytics and session recordings</h2>
            <p>
              We use <a href="https://learn.microsoft.com/en-us/clarity/setup-and-installation/data-collected"
                       target="_blank" rel="noreferrer noopener"
                       className="text-[#A7F3D0] underline">Microsoft Clarity</a> to
              understand how players use Gully — where they get stuck,
              which features they use, and how they navigate the app.
              Clarity captures anonymised heatmaps and session
              recordings of your interactions with the interface.
            </p>
            <p className="mt-3">To protect your privacy we:</p>
            <ul className="mt-2 space-y-2 pl-1">
              <li>· Never record anything on <em>payment, sign-in, or
                  account management</em> pages. Recording is paused
                  the moment you land on those routes.</li>
              <li>· Mask personal identifiers — email addresses, phone
                  numbers, payment fields, names, and other identity
                  inputs — so they never appear in recordings even
                  when captured elsewhere on the page.</li>
              <li>· Leave ordinary coaching conversation text visible
                  in recordings so we can understand where players get
                  confused. Please don't share medical, financial, or
                  government-ID information in Gully chat.</li>
              <li>· Load Clarity asynchronously — it never blocks or
                  slows down the app.</li>
            </ul>
            <p className="mt-3">
              Microsoft Clarity's own data practices are documented at
              <a href="https://www.microsoft.com/en-us/privacy/privacystatement"
                 target="_blank" rel="noreferrer noopener"
                 className="text-[#A7F3D0] underline"> Microsoft's
              privacy statement</a>. Ad-blockers can prevent Clarity
              from loading; nothing else in Gully depends on it.
            </p>
          </section>

          <section data-testid="privacy-meta-disclosure">
            <h2 className="font-display text-lg text-neutral-100 mb-3">Advertising attribution</h2>
            <p>
              We use the <strong>Meta Pixel</strong> on the Gully web
              app to measure whether ads we run on Facebook and
              Instagram actually help cricketers find us. The Pixel
              records standard funnel events — a page view, a chat
              started, a sign-up, a subscription — with your
              pseudonymous browser identifier. It does <em>not</em>
              receive the content of your Gully conversations. On our
              Android app the Pixel does not run.
            </p>
            <p className="mt-3">
              You can opt out of Meta's ad targeting by adjusting
              your Facebook / Instagram ad preferences, and you can
              block the Pixel with any standard content blocker —
              nothing else in Gully depends on it.
            </p>
          </section>

          <section data-testid="privacy-deletion">
            <h2 className="font-display text-lg text-neutral-100 mb-3">Deleting your account</h2>
            <p>
              You can delete your Gully account and everything we
              have stored about you at any time.
            </p>
            <p className="mt-3">
              Visit <Link to="/account/delete"
                          className="text-[#A7F3D0] underline"
                          data-testid="privacy-delete-link">
                askgully.com/account/delete
              </Link> while signed in. After you confirm, we
              permanently remove your account, your conversations,
              your reports, your player memory, any videos you
              uploaded and their analyses, your session history,
              and every internal event we recorded that was linked
              to your account. This action is immediate and cannot
              be undone.
            </p>
            <p className="mt-3">
              Prefer email? Write to <a href={`mailto:${CONTACT_EMAIL}?subject=Delete%20my%20Gully%20account`}
                                        className="text-[#A7F3D0] underline">{CONTACT_EMAIL}</a> from
              the email address on your account and we'll process it
              manually within seven days.
            </p>
          </section>

          <section data-testid="privacy-age">
            <h2 className="font-display text-lg text-neutral-100 mb-3">Who Gully is for</h2>
            <p>
              Gully is intended for cricket players and parents
              aged <strong>13 and above</strong>. If you're under 13,
              please ask a parent or guardian to use Gully with you.
              We do not knowingly collect data from children under
              13; if you believe a child under 13 has created an
              account, please email {CONTACT_EMAIL} and we'll delete
              it.
            </p>
          </section>

          <section data-testid="privacy-contact">
            <h2 className="font-display text-lg text-neutral-100 mb-3">Contact</h2>
            <p>
              Questions, corrections or privacy requests →{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}
                 className="text-[#A7F3D0] underline">{CONTACT_EMAIL}</a>.
            </p>
          </section>
        </div>

        <div className="mt-16 text-sm text-neutral-500">
          <Link to="/" className="text-[#A7F3D0]">Back to gully</Link> · <Link to="/terms" className="text-[#A7F3D0]">Read the terms</Link>
        </div>
      </main>
    </div>
  );
}
